import { buildDocumentCompareTransform, formatDocumentCompareVersionLabel } from '@/lib/snapshot/compare-documents';
import { clearDiffBlockCommentAnchors, restoreCommentAnchors, showDiffBlockCommentAnchors } from '@/lib/snapshot/diff-comment-anchors';
import {
    applySnapshotCompareDiffColorOptions,
    collectUserIdsFromDiffSet,
    enrichDiffAttributions,
    ensureUserDisplayNames,
} from '@/lib/snapshot/snapshot-user';
import { stripCommentMarkup } from '@/lib/snapshot/strip-comment-markup';
import type { MaDocumentWorkStatus } from '@/lib/zybooks-api';
import type { SnapshotVersion } from '@tiptap-pro/extension-snapshot';
import type { Diff, DiffSet } from '@tiptap-pro/extension-snapshot-compare';
import { Fragment, Slice } from '@tiptap/pm/model';
import { ReplaceStep } from '@tiptap/pm/transform';
import type { Editor, JSONContent } from '@tiptap/react';

const RELEASE_TAG = 'release';

export const INITIAL_IMPORT_SNAPSHOT_NAME = 'initial import';

export type InitialSnapshotBootstrapPayload = {
    latestVersion: number;
    versions: SnapshotVersion[];
};

export type ReviewBaseline = {
    version: number;
    name?: string;
};

export function withSnapshotName(baseline: ReviewBaseline | null, versions: SnapshotVersion[]): ReviewBaseline | null {
    if (!baseline || baseline.name != null) {
        return baseline;
    }
    const match = versions.find(version => version.version === baseline.version);
    return match ? { version: match.version, name: match.name } : baseline;
}

export type RunSnapshotCompareResult = { ok: true; fromVersion: number; toVersion: number | undefined } | { ok: false; error: string };

export type RunDocumentCompareResult = { ok: true; diffCount: number } | { ok: false; error: string };

export type StructuralNodeChange = {
    nodeType: string | undefined;
    changeType: 'added' | 'removed';
    from: number;
    to: number;
    attribution: Diff['attribution'];
};

export type PreparedSnapshotDiff = {
    diffs: DiffSet;
    structuralChanges: StructuralNodeChange[];
};

export function shouldBootstrapInitialSnapshot(payload: InitialSnapshotBootstrapPayload): boolean {
    const { latestVersion, versions } = payload;

    if (latestVersion !== 0 || versions.length === 0) {
        return false;
    }

    if (versions.length !== 1 || versions[0].version !== 0) {
        return false;
    }

    const initialVersion = versions[0];
    if (initialVersion.name?.trim() === INITIAL_IMPORT_SNAPSHOT_NAME) {
        return false;
    }

    if (hasReleaseTag(initialVersion)) {
        return false;
    }

    return true;
}

export function findReviewBaseline(versions: SnapshotVersion[]): ReviewBaseline {
    let latestRelease: SnapshotVersion | null = null;

    for (const version of versions) {
        if (hasReleaseTag(version) && (latestRelease === null || version.version > latestRelease.version)) {
            latestRelease = version;
        }
    }

    if (latestRelease) {
        return { version: latestRelease.version, name: latestRelease.name };
    }

    const initialVersion = versions.find(version => version.version === 0);
    return { version: 0, name: initialVersion?.name };
}

export function isLatestReleaseBaseline(baseline: ReviewBaseline, versions: SnapshotVersion[]): boolean {
    return baseline.version === findReviewBaseline(versions).version;
}

export function findReviewBaselineVersion(versions: SnapshotVersion[]): number {
    return findReviewBaseline(versions).version;
}

function formatReviewVersionLabel(baseline: ReviewBaseline): string {
    const nameSuffix = baseline.name?.trim() ? ` — ${baseline.name.trim()}` : '';
    if (baseline.version === 0) {
        return `v0${nameSuffix}`;
    }
    return `v${baseline.version}${nameSuffix}`;
}

export function getReviewBaselineLabel(baseline: ReviewBaseline, compareTo?: ReviewBaseline | null): string {
    if (compareTo) {
        return `${formatReviewVersionLabel(baseline)} → ${formatReviewVersionLabel(compareTo)}`;
    }

    const nameSuffix = baseline.name?.trim() ? ` — ${baseline.name.trim()}` : '';

    if (baseline.version === 0) {
        return `Compare against initial version${nameSuffix}`;
    }

    return `Compare against v${baseline.version}${nameSuffix}`;
}

export function runSnapshotCompare(editor: Editor, fromVersion: number, toVersion?: number): Promise<RunSnapshotCompareResult> {
    return new Promise(resolve => {
        editor
            .chain()
            .compareVersions({
                fromVersion,
                toVersion,
                onCompare: async context => {
                    if ('error' in context && context.error) {
                        resolve({ ok: false, error: context.error.message });
                        return;
                    }

                    if (!('tr' in context)) {
                        resolve({ ok: false, error: 'Compare did not return a diff transaction.' });
                        return;
                    }

                    try {
                        const diffs = await prepareSnapshotDiffSetForDisplay(context.diffSet);
                        applySnapshotCompareDiffColorOptions(editor, { colorByUserId: true });
                        const isDiffMode = editor.commands.showDiff(context.tr, { diffs });
                        if (!isDiffMode) {
                            resolve({ ok: false, error: 'Failed to show diff.' });
                            return;
                        }

                        resolve({ ok: true, fromVersion, toVersion });
                    } catch (error) {
                        resolve({
                            ok: false,
                            error: error instanceof Error ? error.message : 'Failed to resolve authors.',
                        });
                    }
                },
            })
            .run();
    });
}

export function exitSnapshotCompare(editor: Editor): boolean {
    clearDiffBlockCommentAnchors(editor);
    return editor.commands.hideDiff();
}

export type RunDocumentCompareOptions = {
    baseline?: ReviewBaseline;
    compareTo?: ReviewBaseline;
};

export async function runDocumentCompare(
    editor: Editor,
    jsonA: JSONContent,
    jsonB: JSONContent,
    options?: RunDocumentCompareOptions,
): Promise<RunDocumentCompareResult> {
    try {
        const attributionLabels =
            options?.baseline || options?.compareTo
                ? {
                      baselineUserId: options.baseline
                          ? formatDocumentCompareVersionLabel(options.baseline.version, options.baseline.name)
                          : undefined,
                      currentUserId: options.compareTo
                          ? formatDocumentCompareVersionLabel(options.compareTo.version, options.compareTo.name)
                          : 'current',
                  }
                : undefined;
        // Comment threads live in the document, but adding or resolving one is not a content change.
        // Compare without them, then put the newer version's anchors back so comments still show.
        const strippedB = stripCommentMarkup(jsonB);
        const tr = buildDocumentCompareTransform(editor.schema, stripCommentMarkup(jsonA), strippedB, attributionLabels);
        // No differences is still a valid comparison: the version stays selected and the caller says so.
        const diffs = await prepareSnapshotDiffSetForDisplay(tr.toDiff());

        const blockCommentAnchors = restoreCommentAnchors(tr, editor.schema.nodeFromJSON(jsonB), editor.schema.nodeFromJSON(strippedB));
        // showDiff refuses a transform without steps, which is what identical documents produce. A
        // no-op step keeps the comparison open; toDiff() only renders attributed steps, so it shows nothing.
        if (tr.steps.length === 0) {
            tr.step(new ReplaceStep(0, 0, Slice.empty));
        }
        showDiffBlockCommentAnchors(editor, blockCommentAnchors);

        applySnapshotCompareDiffColorOptions(editor, { colorByUserId: false });
        const isDiffMode = editor.commands.showDiff(tr, { diffs });
        if (!isDiffMode) {
            clearDiffBlockCommentAnchors(editor);
            return { ok: false, error: 'Failed to show diff.' };
        }

        return { ok: true, diffCount: diffs.length };
    } catch (error) {
        return {
            ok: false,
            error: error instanceof Error ? error.message : 'Failed to compare documents.',
        };
    }
}

export function collectStructuralNodeChanges(diffSet: DiffSet): StructuralNodeChange[] {
    const changes: StructuralNodeChange[] = [];

    for (const diff of diffSet) {
        if (diff.diffContentType !== 'mark-only' || isGenuineMarkChange(diff)) {
            continue;
        }

        if (diff.type === 'block-delete' || diff.type === 'inline-delete') {
            for (const nodeType of collectNodeTypes((diff as any).content)) {
                changes.push({ nodeType, changeType: 'removed', from: diff.from, to: diff.to, attribution: diff.attribution });
            }
        }

        if (diff.type === 'block-insert' || diff.type === 'inline-insert') {
            for (const nodeType of collectNodeTypes((diff as any).content)) {
                changes.push({ nodeType, changeType: 'added', from: diff.from, to: diff.to, attribution: diff.attribution });
            }
        }
    }

    return changes;
}

export function filterSnapshotDiffSetForDisplay(diffSet: DiffSet): DiffSet {
    return diffSet.filter(diff => {
        if (!diff.attribution.id) {
            return false;
        }

        if (diff.diffContentType == 'content') {
            return true;
        }

        if (diff.diffContentType == 'attribute-only') {
            if (!diff.previousAttributes || !diff.attributes) {
                return false;
            }

            const keys = new Set([...Object.keys(diff.previousAttributes), ...Object.keys(diff.attributes)]);

            for (const key of keys) {
                const previous = diff.previousAttributes[key];
                const current = diff.attributes[key];

                if (previous === current || (previous == null && current == null)) {
                    continue;
                }

                if (
                    typeof previous === 'object' &&
                    previous !== null &&
                    typeof current === 'object' &&
                    current !== null &&
                    JSON.stringify(previous) === JSON.stringify(current)
                ) {
                    continue;
                }

                return true;
            }

            return false;
        }

        if (diff.diffContentType == 'mark-only') {
            return isGenuineMarkChange(diff);
        }

        return false;
    });
}

export async function prepareSnapshotDiffSetForDisplay(diffSet: DiffSet): Promise<DiffSet> {
    const filtered = filterSnapshotDiffSetForDisplay(diffSet);
    const userIds = collectUserIdsFromDiffSet(filtered);
    await ensureUserDisplayNames(userIds);
    return enrichDiffAttributions(filtered);
}

export async function prepareSnapshotDiffSetWithAudit(diffSet: DiffSet): Promise<PreparedSnapshotDiff> {
    const structuralChanges = collectStructuralNodeChanges(diffSet);
    const diffs = await prepareSnapshotDiffSetForDisplay(diffSet);
    return { diffs, structuralChanges };
}

function hasReleaseTag(version: SnapshotVersion): boolean {
    const tags = version.meta?.tags;
    return Array.isArray(tags) && tags.includes(RELEASE_TAG);
}

function fragmentHasVisibleText(content: unknown): boolean {
    if (!content) {
        return false;
    }

    if (content instanceof Fragment) {
        return content.textBetween(0, content.size).length > 0;
    }

    const nodes = Array.isArray(content) ? content : [content];

    for (const node of nodes as any[]) {
        if (!node || typeof node !== 'object') {
            continue;
        }

        if (typeof node.text === 'string' && node.text.length > 0) {
            return true;
        }

        if (node.content && fragmentHasVisibleText(node.content)) {
            return true;
        }
    }

    return false;
}

function isGenuineMarkChange(diff: Diff): boolean {
    return fragmentHasVisibleText((diff as any).content) || fragmentHasVisibleText((diff as any).previousContent);
}

function collectNodeTypes(content: unknown): string[] {
    if (!content) {
        return [];
    }
    const nodes = Array.isArray(content) ? content : [content];
    return nodes
        .filter((node): node is { type: string } => !!node && typeof node === 'object' && typeof (node as any).type === 'string')
        .map(node => node.type);
}

// --- Work-status snapshot metadata, share links, and review range helpers ---

export const WORK_STATUS_TAG = 'work_status';

export type ReviewShareLinkParams = {
    split: boolean;
    diff: boolean;
    from: number | null;
    to: number | null;
};

export type WorkStatusReviewRange = {
    from: ReviewBaseline;
    to: ReviewBaseline | null;
};

export type VersionHighlight = 'endpoint' | 'range' | null;

export function getMaDocumentWorkStatusId(meta: SnapshotVersion['meta']): number | null {
    const raw = (meta as Record<string, unknown> | undefined)?.maDocumentWorkStatusId;
    return typeof raw === 'number' ? raw : null;
}

export type WorkStatusSnapshotAction = 'started' | 'ended';

/** Whether a work status snapshot starts or ends its session, or null for other snapshots. */
export function getWorkStatusSnapshotAction(meta: SnapshotVersion['meta']): WorkStatusSnapshotAction | null {
    const action = (meta as Record<string, unknown> | undefined)?.workStatusAction;
    return action === 'started' || action === 'ended' ? action : null;
}

/**
 * The status a work status snapshot stands for, read from its session row so edits show: the start
 * status on a start snapshot, the end status on an end snapshot. Null when the row is not loaded.
 */
export function getWorkStatusSnapshotStatus(version: SnapshotVersion, workStatuses: MaDocumentWorkStatus[]): string | null {
    const row = getWorkStatusSnapshotRow(version, workStatuses);
    if (!row) {
        return null;
    }
    return getWorkStatusSnapshotAction(version.meta) === 'started' ? row.start_status : row.end_status;
}

/** The session row a work status snapshot points at, or null when it is not loaded. */
export function getWorkStatusSnapshotRow(version: SnapshotVersion, workStatuses: MaDocumentWorkStatus[]): MaDocumentWorkStatus | null {
    const workStatusId = getMaDocumentWorkStatusId(version.meta);
    if (getWorkStatusSnapshotAction(version.meta) == null || workStatusId == null) {
        return null;
    }
    return workStatuses.find(row => row.ma_document_work_status_id === workStatusId) ?? null;
}

export function hasWorkStatusTag(version: SnapshotVersion): boolean {
    const tags = version.meta?.tags;
    return Array.isArray(tags) && tags.includes(WORK_STATUS_TAG);
}

export function formatSnapshotVersionDateTime(timestamp: number): string {
    return new Date(timestamp).toLocaleString(undefined, {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
    });
}

function isEndingWorkStatusVersion(version: SnapshotVersion): boolean {
    return (version.meta as Record<string, unknown> | undefined)?.workStatusAction === 'ended';
}

/** The session row of an ending snapshot; start snapshots carry no comment or duration. */
function getEndedWorkStatus(version: SnapshotVersion, workStatuses: MaDocumentWorkStatus[]): MaDocumentWorkStatus | null {
    const workStatusId = getMaDocumentWorkStatusId(version.meta);
    if (workStatusId == null || !isEndingWorkStatusVersion(version)) {
        return null;
    }
    return workStatuses.find(row => row.ma_document_work_status_id === workStatusId) ?? null;
}

export function getVersionComment(version: SnapshotVersion, workStatuses: MaDocumentWorkStatus[]): string | null {
    const remark = getEndedWorkStatus(version, workStatuses)?.remark?.trim();
    return remark ? remark : null;
}

/** Work duration in minutes for the version card, or null when unset / not applicable. */
export function getVersionWorkDuration(version: SnapshotVersion, workStatuses: MaDocumentWorkStatus[]): number | null {
    return getEndedWorkStatus(version, workStatuses)?.work_duration ?? null;
}

function toReviewBaseline(version: SnapshotVersion): ReviewBaseline {
    return { version: version.version, name: version.name };
}

function parsePositiveVersionParam(searchParams: URLSearchParams, key: string): number | null {
    const raw = searchParams.get(key)?.trim();
    if (!raw) {
        return null;
    }
    const version = Number(raw);
    if (!Number.isInteger(version) || version < 0) {
        return null;
    }
    return version;
}

/** A diff the link asks for: a `from` version, compared with `to` or with the current document. */
export function hasReviewShareDiff(params: ReviewShareLinkParams): boolean {
    return params.diff && params.from != null && params.from !== params.to;
}

/** Whether the link opens split preview or a diff, which the page applies after the editor loads. */
export function hasReviewShareView(params: ReviewShareLinkParams): boolean {
    return params.split || hasReviewShareDiff(params);
}

export function parseReviewShareLinkParams(searchParams: URLSearchParams): ReviewShareLinkParams {
    return {
        split: searchParams.get('split') === 'true',
        diff: searchParams.get('diff') === 'true',
        from: parsePositiveVersionParam(searchParams, 'from'),
        to: parsePositiveVersionParam(searchParams, 'to'),
    };
}

export function getVersionHighlight(
    versionNumber: number,
    fromBaseline: ReviewBaseline | null,
    toBaseline: ReviewBaseline | null,
): VersionHighlight {
    if (fromBaseline?.version === versionNumber || toBaseline?.version === versionNumber) {
        return 'endpoint';
    }
    if (!fromBaseline || !toBaseline) {
        return null;
    }
    const min = Math.min(fromBaseline.version, toBaseline.version);
    const max = Math.max(fromBaseline.version, toBaseline.version);
    if (versionNumber > min && versionNumber < max) {
        return 'range';
    }
    return null;
}

/**
 * Range of version cards for one work session: its start card through its
 * latest card (the end card once closed, or the newest mark while open).
 * `to` is null when the session has only one card so far.
 */
export function getWorkStatusReviewRange(versions: SnapshotVersion[], workStatusId: number): WorkStatusReviewRange | null {
    let start: SnapshotVersion | null = null;
    let end: SnapshotVersion | null = null;
    for (const version of versions) {
        if (getMaDocumentWorkStatusId(version.meta) !== workStatusId) {
            continue;
        }
        if (!start || version.version < start.version) {
            start = version;
        }
        if (!end || version.version > end.version) {
            end = version;
        }
    }
    if (!start || !end) {
        return null;
    }
    return {
        from: toReviewBaseline(start),
        to: start.version === end.version ? null : toReviewBaseline(end),
    };
}

export function derivePreviewPair(
    hoveredVersion: SnapshotVersion | null,
    fromBaseline: ReviewBaseline | null,
    toBaseline: ReviewBaseline | null,
    workStatusVersions: SnapshotVersion[],
): WorkStatusReviewRange | null {
    if (!hoveredVersion) {
        return null;
    }

    const committedCount = Number(Boolean(fromBaseline)) + Number(Boolean(toBaseline));

    if (committedCount === 2) {
        return null;
    }

    if (committedCount === 1) {
        const anchor = fromBaseline ?? toBaseline;
        if (!anchor || anchor.version === hoveredVersion.version) {
            return null;
        }
        const hovered = toReviewBaseline(hoveredVersion);
        const lower = anchor.version < hovered.version ? anchor : hovered;
        const higher = anchor.version < hovered.version ? hovered : anchor;
        return { from: lower, to: higher };
    }

    const workStatusId = getMaDocumentWorkStatusId(hoveredVersion.meta);
    if (workStatusId == null) {
        return null;
    }

    return getWorkStatusReviewRange(workStatusVersions, workStatusId);
}
